import { Modal, Spin } from "antd";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useCookies } from "react-cookie";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toGetMyVehicle, toGetModel } from "../../apis/actions/vehicle.actions";
import { setVehicle } from "../../apis/slices/globalSlice";
import { AppDispatch, RootState } from "../../apis/store";
import { buildSaudiPlateRows } from "../../utils/plateConversions";
import { resolveVehicleModelDisplayName } from "../../utils/vehicleDisplay";

/** Second argument of `resolveVehicleModelDisplayName` — matches Redux `models.list` after fetch. */
type VehicleModelsCatalog = Parameters<
  typeof resolveVehicleModelDisplayName
>[1];

export interface ChooseVehicleModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a car is selected and saved to Redux (sync). Use to continue add-to-cart, etc. */
  onVehicleSelected?: () => void;
}

const ChooseVehicleCarCard = memo(
  function ChooseVehicleCarCard({
    car,
    isSelected,
    modelsList,
    onSelect,
  }: {
    car: any;
    isSelected: boolean;
    modelsList: VehicleModelsCatalog;
    onSelect: (car: any) => void;
  }) {
    const { t } = useTranslation();

    const { modelName, plateTitle, plateAr, plateEn, hasPlate } = useMemo(() => {
      const modelNameInner = resolveVehicleModelDisplayName(car, modelsList);
      const plateNumsRaw = String(car?.plate_numbers ?? "").trim();
      const plateLetsRaw = String(car?.plate_letters ?? "").trim();
      const hasPlateInner = Boolean(plateNumsRaw || plateLetsRaw);
      const plateRows = hasPlateInner
        ? buildSaudiPlateRows(car?.plate_numbers, {
            plateLetters: car?.plate_letters,
          })
        : null;
      const plateArInner = plateRows
        ? `${plateRows.arNums} ${plateRows.arLetters}`.replace(/\s+/g, " ").trim()
        : "";
      const plateEnInner = plateRows
        ? `${plateRows.enNums} ${plateRows.enLetters}`.replace(/\s+/g, " ").trim()
        : "";
      const plateTitleInner =
        hasPlateInner && plateEnInner && plateArInner
          ? `${plateEnInner} · ${plateArInner}`
          : undefined;
      return {
        modelName: modelNameInner,
        plateTitle: plateTitleInner,
        plateAr: plateArInner,
        plateEn: plateEnInner,
        hasPlate: hasPlateInner,
      };
    }, [car, modelsList]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(car);
        }
      },
      [car, onSelect]
    );

    const handleClick = useCallback(() => {
      onSelect(car);
    }, [car, onSelect]);

    return (
      <div
        className={`ac-canvas-car-card${
          isSelected ? " ac-canvas-car-card--selected" : ""
        }`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-current={isSelected ? "true" : undefined}
        onKeyDown={handleKeyDown}
      >
        {isSelected ? (
          <span
            className="ac-canvas-car-card__selected-mark"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                fill="currentColor"
              />
            </svg>
          </span>
        ) : null}
        <div className="ac-canvas-car-card__body">
          <div className="ac-canvas-car-card__media">
            <div className="ac-canvas-car-card__thumb">
              <img
                className="ac-canvas-car-card__thumb-img"
                src={car.image_128}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="ac-canvas-car-card__details">
            <dl className="ac-canvas-car-card__specs">
              <div className="ac-canvas-car-card__spec">
                <dt className="ac-canvas-car-card__label">
                  {t("profile.car.title")}
                </dt>
                <dd
                  className="ac-canvas-car-card__value"
                  title={car.title || undefined}
                >
                  {car.title}
                </dd>
              </div>
              <div className="ac-canvas-car-card__spec">
                <dt className="ac-canvas-car-card__label">
                  {t("profile.car.model")}
                </dt>
                <dd
                  className="ac-canvas-car-card__value"
                  title={modelName || undefined}
                >
                  {modelName}
                </dd>
              </div>
              <div className="ac-canvas-car-card__spec">
                <dt className="ac-canvas-car-card__label">
                  {t("profile.car.vin")}
                </dt>
                <dd
                  className="ac-canvas-car-card__value ac-canvas-car-card__value--mono"
                  dir="ltr"
                  title={car.vin_sn || undefined}
                >
                  {car.vin_sn}
                </dd>
              </div>
              <div className="ac-canvas-car-card__spec ac-canvas-car-card__spec--plate">
                <dt className="ac-canvas-car-card__label">
                  {t("profile.car.plate")}
                </dt>
                <dd
                  className="ac-canvas-car-card__value ac-canvas-car-card__value--plate-bilingual"
                  title={plateTitle}
                >
                  <div className="ac-canvas-car-card__plate-container">
                    {!hasPlate ? (
                      <span className="ac-canvas-car-card__plate-empty">—</span>
                    ) : (
                      <div className="ac-canvas-car-card__plate-bilingual">
                        <div className="ac-canvas-car-card__plate-line">
                          <span className="ac-canvas-car-card__plate-lang">
                            {t("profile.car.plateLineArabic")}
                          </span>
                          <span
                            className="ac-canvas-car-card__plate-text"
                            dir="rtl"
                            lang="ar"
                          >
                            {plateAr}
                          </span>
                        </div>
                        <div className="ac-canvas-car-card__plate-line">
                          <span className="ac-canvas-car-card__plate-lang">
                            {t("profile.car.plateLineEnglish")}
                          </span>
                          <span
                            className="ac-canvas-car-card__plate-text"
                            dir="ltr"
                            lang="en"
                          >
                            {plateEn}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.car === next.car &&
    prev.isSelected === next.isSelected &&
    prev.modelsList === next.modelsList &&
    prev.onSelect === next.onSelect
);

/**
 * Modal picker for “which car?” — same cards as the navbar car canvas, optimized for services flow.
 */
function ChooseVehicleModalInner({
  open,
  onClose,
  onVehicleSelected,
}: ChooseVehicleModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const [cookies] = useCookies(["apiKey"]);
  const myVehicles = useSelector(
    (state: RootState) => state?.vehicle?.myVehicles
  );
  const models = useSelector((state: RootState) => state?.vehicle?.models);
  const selectedVehicleId = useSelector(
    (state: RootState) => state?.global?.vehicle?.id
  );

  /** Lang of the last successful `toGetMyVehicle` response (set only when status flips to succedded). */
  const lastSuccessfulVehiclesLangRef = useRef<string | null>(null);
  const prevVehiclesStatusRef = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    const s = myVehicles?.status;
    if (s === "succedded" && prevVehiclesStatusRef.current !== "succedded") {
      lastSuccessfulVehiclesLangRef.current = i18n.language;
    }
    prevVehiclesStatusRef.current = s;
  }, [myVehicles?.status, i18n.language]);

  useEffect(() => {
    if (!open || !cookies.apiKey) return;

    if (myVehicles?.status === "loading") return;

    const modelsReady =
      models?.status === "succedded" &&
      Array.isArray(models?.list) &&
      models.list.length > 0;

    const vehiclesCachedForLang =
      myVehicles?.status === "succedded" &&
      lastSuccessfulVehiclesLangRef.current === i18n.language;

    if (vehiclesCachedForLang) {
      if (!modelsReady) {
        dispatch(toGetModel());
      }
      return;
    }

    dispatch(toGetMyVehicle(i18n.language));
    if (!modelsReady) {
      dispatch(toGetModel());
    }
  }, [
    open,
    cookies.apiKey,
    dispatch,
    i18n.language,
    myVehicles?.status,
    models?.list?.length,
    models?.status,
  ]);

  const handleSelectCar = useCallback(
    (car: any) => {
      dispatch(setVehicle(car));
      onVehicleSelected?.();
      onClose();
    },
    [dispatch, onClose, onVehicleSelected]
  );

  const list = myVehicles?.list;
  const listLen = Array.isArray(list) ? list.length : 0;
  const status = myVehicles?.status;

  const showSpinner = useMemo(() => {
    if (!open) return false;
    if (listLen > 0) return false;
    if (status === "succedded" || status === "rejected") return false;
    return status === "loading" || status === "idle";
  }, [open, listLen, status]);

  const modalClassNames = useMemo(
    () => ({ body: "ac-choose-vehicle-modal__body" }),
    []
  );

  const modelsList = models?.list as VehicleModelsCatalog;

  const carRows = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((el: any) => (
      <ChooseVehicleCarCard
        key={el.id ?? el.vin_sn}
        car={el}
        isSelected={selectedVehicleId === el.id}
        modelsList={modelsList}
        onSelect={handleSelectCar}
      />
    ));
  }, [handleSelectCar, list, modelsList, selectedVehicleId]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose={false}
      centered
      width="min(100%, 440px)"
      zIndex={1100}
      classNames={modalClassNames}
      className="ac-choose-vehicle-modal"
      title={
        <div className="ac-choose-vehicle-modal__head">
          <h2 className="ac-choose-vehicle-modal__title ac--font-expo">
            {t("services.chooseVehicle.title")}
          </h2>
          <p className="ac-choose-vehicle-modal__subtitle">
            {t("services.chooseVehicle.subtitle")}
          </p>
        </div>
      }
    >
      <div className="ac-choose-vehicle-modal__scroll">
        {showSpinner ? (
          <div className="ac-choose-vehicle-modal__loading">
            <Spin size="large" />
          </div>
        ) : listLen > 0 ? (
          <div className="ac-canvas-my-cars__list ac-choose-vehicle-modal__list">
            {carRows}
          </div>
        ) : (
          <div className="ac-choose-vehicle-modal__empty">
            <p className="ac-choose-vehicle-modal__empty-text ac--font-expo">
              {t("services.chooseVehicle.empty")}
            </p>
            <Link
              to="/add-car"
              className="ac-choose-vehicle-modal__add-car btn btn-primary rounded-3 ac--font-expo"
              onClick={onClose}
            >
              {t("services.chooseVehicle.addCarCta")}
            </Link>
          </div>
        )}
      </div>
    </Modal>
  );
}

const ChooseVehicleModal = memo(ChooseVehicleModalInner);

export default ChooseVehicleModal;
